import { offset, useFloating } from '@floating-ui/react-dom-interactions'
import { debounce } from 'lodash'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { SUPPORTED_FONTS } from 'prose-core'
import { useContext, useEffect, useMemo } from 'react'
import scrollIntoView from 'scroll-into-view-if-needed'
import { FontFamilyType } from 'utils/fontUtils'
import { useForwardRef } from '../../../../../hooks/useForwardRef'
import { FontFamilyDropdownButton } from './FontFamilyDropdownButton'
import Styles from './FontFamilyGroup.module.scss'
import { FontFamilyContext } from './stores/FontFamilyStore'

export const FontFamilyDropdownBody = observer(function FontFamilyDropdownBody() {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const fontFamilyStore = useContext(FontFamilyContext)!

  const { x, y, reference, floating, strategy } = useFloating({
    // DRA-2211 - For some reason, using the 'fixed' strategy here results in negative y values, as if the library
    // is calculating the offset relative to the bottom of the viewport which is where #floating-ui-root lives.
    strategy: 'absolute',
    placement: 'bottom-start',
    middleware: [offset(4)],
  })
  useEffect(() => reference(fontFamilyStore.fontFamilyAnchor))
  useEffect(() => reaction(() => fontFamilyStore.fontFamilyAnchor, reference), [fontFamilyStore, reference])
  // TODO: Fix this the next time the file is edited.
  /* eslint-disable-next-line no-nested-ternary */
  const availableHeight = y == null ? 0 : y > 0 ? window.innerHeight - y : -y
  const style = useMemo(
    () => ({ height: `${availableHeight - 40}px`, position: strategy, top: y ?? 0, left: x ?? 0 }),
    [strategy, x, y, availableHeight],
  )
  const bodyRef = useForwardRef<HTMLDivElement>(floating)

  // Hide on hover blur
  useEffect(() => {
    const handleMouseOver = debounce(fontFamilyStore.handleWindowClick, 150, { leading: false, trailing: true })
    window.addEventListener('mouseover', handleMouseOver)
    return () => window.removeEventListener('mouseover', handleMouseOver)
  }, [fontFamilyStore])

  // Hide on click blur
  useEffect(() => {
    window.addEventListener('click', fontFamilyStore.handleWindowClick)
    return () => window.removeEventListener('click', fontFamilyStore.handleWindowClick)
  }, [fontFamilyStore])

  useEffect(() => {
    if (!fontFamilyStore.showingFontFamilyDropdown) return
    setTimeout(() => {
      const activeFont = bodyRef.current?.querySelector('[data-active-font-family="true"]')
      if (activeFont) {
        scrollIntoView(activeFont, {
          scrollMode: 'if-needed',
          block: 'center',
          inline: 'nearest',
        })
      }
    }, 0)
  }, [fontFamilyStore, bodyRef])

  if (!fontFamilyStore.showingFontFamilyDropdown) return null

  if (!y) return <div ref={bodyRef}></div>

  return (
    <div ref={bodyRef} className={Styles.dropdownBody} style={style}>
      {SUPPORTED_FONTS.map((fontName: FontFamilyType) => (
        <FontFamilyDropdownButton key={fontName} fontName={fontName} />
      ))}
    </div>
  )
})
